import dayjs from 'dayjs'
import _isEmpty from 'underscore/cjs/isEmpty.js'
import _pick from 'underscore/cjs/pick.js'

import { encrypt } from '@/helpers/crypto.helpers'
import { composeMedicareName } from '@/utils/health/medicare-name'

import {
  BRAZE_APPLICATION_START,
  BRAZE_APPLICATION_PROGRESS,
  PERSONAL_DETAILS_STEP_NUMBER,
  PARTNER_DETAILS_STEP_NUMBER,
  CHILDREN_DETAILS_STEP_NUMBER,
  REBATE_STEP_NUMBER,
  LHC_STEP_NUMBER,
  CHOOSE_EXTRAS_STEP_NUMBER,
  MEDICARE_STEP_NUMBER,
  PAYMENT_STEP_NUMBER,
  ACKNOWLEDGEMENT_STEP_NUMBER,
  REVIEW_STEP_NUMBER
} from '~/constants/application/health-insurance-v2'

export const useHealthApplicationStore = defineStore('health-application-v2', {
  state: () => ({
    form: {},
    lastStep: undefined,
    activeRebates: [],
    applicationData: {},
    productDetails: {},
    productId: '',
    leadUuid: '',
    salesforceId: '',
    sfAddressPrefilled: false,

    // braze
    brazeEventConfig: {
    },
    brazeSentSteps: {
      'your_details': false,
      'rebate_details': false,
      'lhc_loading': false,
      'extras_selection': false,
      'medicare_details': false,
      'payment_details': false
    },
    applicationId: null
  }),

  actions: {
    async updateApplication ({ uuid, product_id, current_step, life_stage, salesforce_id, from_customer_quote, application_id, leadRefineParams, IS_QRP_LANDING = false, application_in_progress_at_step1 = false, promo }) {
      const form = this.form

      const formData = flattenObj(form, 1)

      const members = []

      members.push({
        type: 'primary',
        first_name: formData.first_name,
        last_name: formData.last_name,
        middle_name: formData.middle_name,
        email: formData.email,
        mobile: formData.mobile,
        phone: formData.phone,
        dob: formData.birthdate ? dayjs(formData.birthdate, 'DD-MM-YYYY').format('MM/DD/YYYY') : '',
        gender: formData.gender,
        title: formData.title,
        currently_insured: formData.is_covered == 'Yes',
        current_insurer: formData.is_covered == 'Yes' ? (formData.insurer === 'other' ? formData.insurer_other : formData.insurer) : '',
        preferred_contact_method: formData.preferred_contact_method,
        current_member_no: formData.is_covered == 'Yes' ? formData.membership_id : undefined,
        lhc_age: formData.lhc_age_primary ?? '',
        cont_cover: formData.held_hospital_cover_continuously == 'Yes'
      })

      const parnterValues = _pick(formData, [
        'partner_first_name',
        'partner_last_name',
        'partner_middle_name',
        'partner_birthdate',
        'partner_gender',
        'partner_mobile',
        'partner_email',
        'partner_title',
        'is_covered_partner',
        'partner_insurer',
        'partner_membership_id',
        'lhc_age_partner',
        'partner_held_hospital_cover_continuously'
      ])

      if (['couple', 'family', 'extended family'].includes(life_stage) && !_isEmpty(parnterValues)) {
        members.push({
          type: 'partner',
          first_name: formData.partner_first_name,
          last_name: formData.partner_last_name,
          middle_name: formData.partner_middle_name,
          mobile: formData.partner_mobile,
          email: formData.partner_email,
          dob: formData.partner_birthdate ? dayjs(formData.partner_birthdate, 'DD-MM-YYYY').format('MM/DD/YYYY') : '',
          gender: formData.partner_gender,
          title: formData.partner_title,
          currently_insured: formData.is_covered_partner == 'Yes',
          current_insurer: formData.is_covered_partner == 'Yes' ? (formData.partner_insurer === 'other' ? formData.partner_insurer_other : formData.partner_insurer) : '',
          current_member_no: formData.is_covered_partner == 'Yes' ? formData.partner_membership_id : undefined,
          lhc_age: formData.lhc_age_partner ?? '',
          cont_cover: formData.partner_held_hospital_cover_continuously == 'Yes'
        })
      }

      let children = []
      const childrenForm = formData.children
      if (['single parent', 'family', 'extended family', 'extended single parent'].includes(life_stage) && !_isEmpty(childrenForm) && childrenForm.length && childrenForm?.some(child => (Object.values(child).filter(e => e)).length)) {
        children = childrenForm?.map(child => {
          return {
            type: 'child',
            first_name: child.child_first_name,
            last_name: child.child_last_name,
            middle_name: child.child_middle_name,
            gender: child.child_gender,
            dob: child.child_birth_date ? dayjs(child.child_birth_date, 'DD-MM-YYYY').format('MM/DD/YYYY') : '',
            university_name: child.uni_name,
            university_year: !_isEmpty(child.uni_completion_date) ? dayjs(child.uni_completion_date, 'DD-MM-YYYY').format('MM/DD/YYYY') : '',
            is_student: child.is_child_student,
            currently_insured: child.is_child_covered == 'Yes',
            current_insurer: child.child_current_fund
          }
        })
      }

      const body = {
        uuid,
        home_address: formData.street_address,
        postal_address: _isEmpty(formData.postal_is_same_as_residential) ? formData.post_street_address : undefined,
        product_id,
        members,
        children,
        current_step,
        frequency: formData.payment_frequency,
        salesforce_id,
        life_stage,
        direct_debit: formData.payment_method == 'Direct debit' ? 'yes' : 'no',
        from_customer_quote: from_customer_quote ? 'yes' : 'no',
        application_id,
        refineParams: leadRefineParams
      }

      // quote wins — see selectedProduct in applicationV2/[step].vue. selected_product
      // lags a product switch, so it is the fallback for when the quote re-fetch is empty.
      const insurerName = (this.productDetails?.quote?.insurer?.name || this.applicationData?.selected_product?.insurer || '').toLowerCase()
      // Only HBF/See-U are asked. Array = answered; '' after a reload past step1
      // would otherwise send 'no' and overwrite a stored 'yes'.
      const consent = formData.insurer_marketing_consent
      if (['hbf', 'see-u'].includes(insurerName) && Array.isArray(consent)) {
        body.insurer_marketing_consent = consent.includes('Yes') ? 'yes' : 'no'
      }

      if(!_isEmpty(formData.income)) {
        body.income_tier = formData.income
      }

      if (!_isEmpty(formData.choosable_extras)) {
        body.choosable_extras = formData.choosable_extras
          .map(extra => Number(`${extra}`.replace('extra-', '')))
          .filter(id => Number.isFinite(id))
      }

      if (!_isEmpty(childrenForm) && childrenForm?.length) {
        body.total_dependants = childrenForm.length
      }

      const medicareForm = _pick(formData, [
        'medicare_card_number',
        'medicare_expired',
        'medicare_name',
        'medicare_first_name',
        'medicare_middle_name',
        'medicare_last_name',
        'medicare_color'
      ])
      if (!_isEmpty(medicareForm) && !_isEmpty(formData.medicare_card_number)) {
        const medicareColorToType = { Blue: 'Interim', Yellow: 'Reciprocal', Green: 'Resident' }
        const expiryParts = formData.medicare_expired?.split('/') ?? []

        // Redesign (HI-1288) captures first/middle/last; recompose to the single
        // `medicare_name` string the API/Salesforce expects. Fall back to the legacy
        // single field when the split fields are absent (flag off / older data).
        const composedName = composeMedicareName({
          firstName: formData.medicare_first_name,
          middleName: formData.medicare_middle_name,
          lastName: formData.medicare_last_name
        })

        body.medicare = {
          card_number: formData.medicare_card_number,
          name: composedName || formData.medicare_name,
          type: medicareColorToType[formData.medicare_color] || formData.medicare_color
        }

        if (expiryParts.length === 3) {
          body.medicare.exp_day = expiryParts[0]
          body.medicare.exp_month = expiryParts[1]
          body.medicare.exp_year = expiryParts[2]
        } else {
          body.medicare.exp_month = expiryParts[0]
          body.medicare.exp_year = expiryParts[1]
        }

        // entitlement is always the 'Yes'/'No' string in the form model (the redesign
        // checkbox maps to/from it at the binding), so it passes straight through
        body.medicare_entitlement = formData.medicare_entitlement?.includes('Yes') ? 'Yes' : 'No'
      }

      if (!_isEmpty(formData.payment_method) && current_step >= PAYMENT_STEP_NUMBER) {
        const method = formData.payment_method == 'Credit card' ? 'credit' : 'bank'
        const payment = {
          payment_type: method,
          payment_details: {
            ...( method == 'bank' ?
              {
                type: 'acc',
                acc_name: encrypt(formData.bank_acc_name),
                acc_number: encrypt(formData.bank_no),
                acc_bsb: encrypt(formData.bsb),
                acc_bank: encrypt(formData.bank_name)

              }: {
                token: formData.cc_data?.token,
                name: formData.cc_data?.name ?? formData.cardholder_name,
                cc_month: formData.cc_data?.cc_month ?? formData.card_exp_date?.split('/')[0],
                cc_year: formData.cc_data?.cc_year ?? formData.card_exp_date?.split('/')[1],
                cc_type: (formData.cc_data?.type ?? formData.card_type) ?? undefined,
                card_number: formData.cc_data?.card_number ?? formData.card_number ?? ''
              }
            )
          }
        }
        body.payment = payment

        if (insurerName === 'hbf' && formData.payment_method === 'Direct debit') {
          body.same_account_for_claims = formData.same_account === 'Yes'

          if (formData.same_account === 'No' && !_isEmpty(formData.credit_bank_no) && !_isEmpty(formData.direct_credit_account_name) && !_isEmpty(formData.credit_bsb)) {
            body.credit_details = {
              acc_name: encrypt(formData.direct_credit_account_name),
              acc_number: encrypt(formData.credit_bank_no),
              acc_bsb: encrypt(formData.credit_bsb),
              type: 'acc'
            }
          }
        }

        body.policy_start_date = formData.policy_start_date ? dayjs(formData.policy_start_date, 'DD-MM-YYYY').format('MM/DD/YYYY') : ''
      }

      body.agree_to_acknowledgement = !!(formData.acknowledgement ?? false)
      body.IS_QRP_LANDING = IS_QRP_LANDING

      // HI-1551 — sent only when the Split flag pair resolved on, mirroring IS_QRP_LANDING. The
      // API compares with strict === true, so an absent key is what keeps today's behaviour.
      if (application_in_progress_at_step1 === true) {
        body.APPLICATION_IN_PROGRESS_AT_STEP1 = true
      }

      // HI-1697 — omitted when empty so the backend never clears a stored promo.
      if (promo) {
        body.promo = promo
      }

      const response = await $fetch('/api/health-insurance/application/update', {
        method: 'post',
        body
      })

      return { id: response?.data?.application, uuid: response?.data?.application_uuid }
    },

    async finishAction ({ uuid, salesforce_id }) {
      const response = await $fetch('/api/health-insurance/application-v2/finish', {
        method: 'post',
        body: {
          uuid,
          salesforce_id
        }
      })

      console.log('%cFinish Action', 'color: green', response)
    },

    reset () {
      this.form = {}
      this.lastStep = undefined
      this.productDetails = {}
      this.applicationData = {}
      this.productId = ''
      this.sfAddressPrefilled = false
    },

    brazeEventConfigInit ({ proceeding_frequency, step, anonymous = false }) {
      const form = this.form
      const formData = flattenObj(form, 1)

      const data = {
        // proceeding_frequency,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.mobile,
        step,
        product_id: this.productId
      }

      data.session_id = this.brazeEventConfig?.session_id ?? undefined

      if (anonymous) {
        // generate random session id that persists across steps for anonymous users
        data.session_id = crypto.randomUUID()
      }

      if (_isEmpty(this.brazeEventConfig) && sessionStorage.getItem('brazeEventConfig')) {
        this.brazeEventConfig = JSON.parse(sessionStorage.getItem('brazeEventConfig'))
      } else {
        this.brazeEventConfig = data
      }

      sessionStorage.setItem('brazeEventConfig', JSON.stringify(data))
    },

    async brazeEvent (evt, { step, proceeding_frequency, life_stage, product_id, anonymous = false }) {
      const progressMap = {
        [`${PERSONAL_DETAILS_STEP_NUMBER}`]: 'your_details',
        [`${PARTNER_DETAILS_STEP_NUMBER}`]: 'your_details',
        [`${CHILDREN_DETAILS_STEP_NUMBER}`]: 'your_details',
        [`${REBATE_STEP_NUMBER}`]: 'rebate_details',
        [`${LHC_STEP_NUMBER}`]: 'lhc_loading',
        [`${CHOOSE_EXTRAS_STEP_NUMBER}`]: 'extras_selection',
        [`${MEDICARE_STEP_NUMBER}`]: 'medicare_details',
        [`${PAYMENT_STEP_NUMBER}`]: 'payment_details',
        [`${ACKNOWLEDGEMENT_STEP_NUMBER}`]: 'acknowledgement',
        [`${REVIEW_STEP_NUMBER}`]: 'review'
      }

      const form = this.form

      const formData = flattenObj(form, 1)

      const quote = this.productDetails.quote

      const body = {
        application_id: this.applicationId ?? null,
        event: evt,
        proceeding_price: quote?.available_premiums[0]?.formatted_amount,
        proceeding_frequency: proceeding_frequency,
        product_id: product_id ?? this.productId
      }

      /**
       * use applicationData to get the original values to persists across steps
       * use brazeEventConfig to get persist only to the next step
      **/
      const primaryMember = this.applicationData?.members?.find(member => member.type == 'primary')
      // const primaryMember = {
      //   first_name: this.brazeEventConfig?.first_name,
      //   last_name: this.brazeEventConfig?.last_name,
      //   email: this.brazeEventConfig?.email,
      //   mobile: this.brazeEventConfig?.phone
      // }

      let userDetailUpdatedFlag = false
      if (progressMap[step]) {
        body.progress_stage = progressMap[step]

        // flag for updated details only for phone, email, and name changes
        userDetailUpdatedFlag = (primaryMember?.email !== formData.email ||
          primaryMember?.mobile !== formData.mobile ||
          primaryMember?.first_name !== formData.first_name ||
          primaryMember?.last_name !== formData.last_name)

        body.user_details_updated = userDetailUpdatedFlag
        body.original_email = this.brazeEventConfig?.email
        body.original_phone = this.brazeEventConfig?.phone
        body.original_first_name = this.brazeEventConfig?.first_name
        body.original_last_name = this.brazeEventConfig?.last_name

        // if original body is empty with a session id, means application started w/ anonymous user
        if (_isEmpty(body.original_email) && this.brazeEventConfig?.session_id) {
          body.original_email = formData.email
          body.original_phone = formData.mobile
          body.original_first_name = formData.first_name
          body.original_last_name = formData.last_name
          userDetailUpdatedFlag = false
          body.user_details_updated = false
        }
      }

      this.brazeEventConfigInit({
        proceeding_frequency: proceeding_frequency,
        // if user details updated, use current step value to remove persistence of progress event in following steps
        step: userDetailUpdatedFlag ? this.brazeEventConfig.step : step,
        anonymous
      })

      /**
       *  braze event stage definition refers to YOUR DETAILS, REBAT DETAILS, LHC LOADING, MEDICARE DETAILS, PAYMENT DETAILS
       * if scale is non-single, unset the stages that are not applicable for event
       * i.e grouped steps 1,2,3 refers to YOUR DETAILS
      */
      if (life_stage) {
        if (life_stage !== 'single') {
          progressMap['1'] = undefined
        }
        if (life_stage.includes('family')) {
          progressMap['2'] = undefined
        }

        if (progressMap[step] === undefined) {
          body.progress_stage = undefined
        }
      }

      // allow event to be sent for the first STAGE only after user details updated
      if (userDetailUpdatedFlag && progressMap[step] === 'your_details') {
        this.brazeSentSteps['your_details'] = false
      }

      if (this.brazeSentSteps[progressMap[step]] && evt === BRAZE_APPLICATION_PROGRESS) {
        return
      }

      // flag the already sent event to prevent duplicate events
      if (progressMap[step]) {
        this.brazeSentSteps[progressMap[step]] = true
      }

      if (progressMap[step] == undefined && evt !== BRAZE_APPLICATION_START && !userDetailUpdatedFlag) {
        return
      }

      body.session_id = this.brazeEventConfig?.session_id ?? undefined
      
      await $fetch('/api/health-insurance/application-v2/braze/track-user', {
        method: 'post',
        body
      })

      // remove session id after sending it once to the progress event
      if (evt === BRAZE_APPLICATION_PROGRESS) {
        this.brazeEventConfig.session_id = undefined
      }
    }
  }
})